import {
  COMPLETION_ITEM_LABEL,
  COMPLETION_ITEMS,
  COMPLETION_OUTCOME_LABEL,
  type CompletionItem,
  type CompletionOutcome,
} from "./completion-picker";
import {
  PRESCRIPTION_CHOICE_LABEL,
  type PrescriptionChoice,
} from "./prescription-choice";

// TES-1058 (Emelie, TES-1054 point A) — the structured decision as journal
// text.
//
//   "Strukturerade beslut, motivering, begärda kompletteringar, receptstatus och plan ska omvandlas till journaltext under rätt rubriker."
//   "Gränssnittets kryssrutor ska inte kopieras som en rå checklista."
//
// and point B:
//
//   "Om underlaget inte räcker beskriver anteckningen den preliminära bedömningen, att slutligt beslut avvaktar och exakt vilka kompletteringar som begärs."
//
// Fixed sentences around what the doctor chose, never an interpretation:
// the outcome, the items and the prescription status are the labels he
// picked, the motivation and care path are his own words. The doctor's
// assessment itself stays his to write in "Bedömning"; these lines lead the
// box when the note is assembled and are never written into it (the same
// rule as the order's "Ordination" line, TES-956).

export interface JournalDecision {
  /** The outcome as the note names it (a label, not a code). */
  outcome?: string | null;
  /**
   * The decision waits for what was requested: the note says the assessment
   * is preliminary and the final decision pending.
   */
  pending?: boolean;
  /** The kompletteringar asked for, and the heading of each "other" one. */
  completion?: {
    items: readonly CompletionItem[];
    otherHeadings?: readonly string[];
    /** When they were asked for, for a note written later than that. */
    requestedAt?: string | null;
  } | null;
  /**
   * TES-1095 — the monthly review continues the order the patient is on, as
   * it is (monthlyTreatmentUnchanged).
   */
  treatmentUnchanged?: boolean;
  /** The prescription status chosen in the Webdoc step. */
  prescription?: {
    choice: PrescriptionChoice;
    existingValidUntil?: string | null;
  } | null;
  /**
   * TES-1185 — after kompletteringar: the planned treatment kept, or
   * changed to `order` (prose) for `reason`.
   */
  plannedTreatment?:
    | { choice: "kept" }
    | { choice: "changed"; reason: string; order?: string | null }
    | null;
  /** The doctor's motivation, verbatim. */
  motivation?: string | null;
  /** The care path given to the patient ("Fortsatt vårdväg"), verbatim. */
  carePath?: string | null;
}

// Emelie's words (point B), as one fixed sentence.
export const JOURNAL_PRELIMINARY_LINE =
  "Preliminär bedömning. Slutligt beslut avvaktar begärda kompletteringar.";

// TES-1185 — A-02 2026-10-08: "Om planen kvarstår kan anteckningen ange att
// begärda kompletteringar har bedömts, att inget har framkommit som ändrar den
// planerade behandlingen och att recept har utfärdats i Webdoc." The first
// two as one line; the prescription is the Receptstatus line.
export const JOURNAL_PLAN_KEPT_LINE =
  "Begärda kompletteringar har bedömts. Inget har framkommit som ändrar den planerade behandlingen.";

// TES-1095 (Emelie, TES-1093 rule 3) — an unchanged monthly review keeps the
// same note structure, and under the ordination and the prescription status
// it says so:
// "Under ordination och receptstatus ska det framgå att behandlingen fortsätter oförändrad och att befintligt recept fortfarande gäller."
// Her words as one sentence; "Befintligt recept gäller fortfarande" is
// already the Receptstatus line's own label.
export const JOURNAL_TREATMENT_UNCHANGED_LINE =
  "Behandlingen fortsätter oförändrad.";

function sentence(s: string): string {
  const t = s.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

/** The requested kompletteringar by name, "other" as its own heading. */
export function requestedCompletionText(
  completion: NonNullable<JournalDecision["completion"]>,
): string | null {
  const names: string[] = [];
  for (const item of COMPLETION_ITEMS) {
    if (!completion.items.includes(item)) continue;
    const headings = (completion.otherHeadings ?? [])
      .map((h) => h.trim())
      .filter(Boolean);
    if (item === "other" && headings.length > 0) names.push(...headings);
    else names.push(COMPLETION_ITEM_LABEL[item]);
  }
  return names.length > 0 ? names.join("; ") : null;
}

/**
 * The decision's lines: what leads "Bedömning" (outcome, the preliminary
 * sentence, motivation) and what leads the plan box (requested
 * kompletteringar, prescription status, care path). Empty arrays when
 * nothing was decided.
 */
export function journalDecisionLines(
  decision: JournalDecision | null | undefined,
): { assessment: string[]; plan: string[] } {
  const assessment: string[] = [];
  const plan: string[] = [];
  if (!decision) return { assessment, plan };
  if (decision.outcome?.trim()) {
    assessment.push(`Utfall: ${sentence(decision.outcome)}`);
  }
  if (decision.pending) assessment.push(JOURNAL_PRELIMINARY_LINE);
  if (decision.plannedTreatment?.choice === "kept") {
    assessment.push(JOURNAL_PLAN_KEPT_LINE);
  } else if (decision.plannedTreatment?.choice === "changed") {
    const to = decision.plannedTreatment.order?.trim();
    assessment.push(
      to
        ? `Planerad läkemedelsbehandling ändrad till ${to}.`
        : "Planerad läkemedelsbehandling ändrad.",
    );
    assessment.push(
      `Medicinsk motivering: ${sentence(decision.plannedTreatment.reason)}`,
    );
  }
  if (decision.motivation?.trim()) {
    assessment.push(`Motivering: ${sentence(decision.motivation)}`);
  }
  const requested = decision.completion
    ? requestedCompletionText(decision.completion)
    : null;
  if (requested) {
    const day = decision.completion?.requestedAt?.slice(0, 10);
    plan.push(
      day
        ? `Begärda kompletteringar (${day}): ${requested}.`
        : `Begärda kompletteringar: ${requested}.`,
    );
  }
  if (decision.treatmentUnchanged) plan.push(JOURNAL_TREATMENT_UNCHANGED_LINE);
  if (decision.prescription) {
    const { choice, existingValidUntil } = decision.prescription;
    const until =
      choice === "existing_valid" && existingValidUntil
        ? ` (gäller till ${existingValidUntil})`
        : "";
    plan.push(`Receptstatus: ${PRESCRIPTION_CHOICE_LABEL[choice]}${until}.`);
  }
  if (decision.carePath?.trim()) {
    plan.push(`Fortsatt vårdväg: ${sentence(decision.carePath)}`);
  }
  return { assessment, plan };
}

// ---- per note ---------------------------------------------------------------

/**
 * The care meeting's decision (TES-950's picker): its outcome, and on
 * "Komplettering behövs" the items and the preliminary sentence.
 */
export function meetingJournalDecision(
  completion:
    | {
        outcome: CompletionOutcome | string | null;
        items?: readonly string[] | null;
        otherHeading?: string | null;
      }
    | null
    | undefined,
): JournalDecision | null {
  const outcome = completion?.outcome;
  if (!outcome || !(outcome in COMPLETION_OUTCOME_LABEL)) return null;
  const asked = outcome === "completion";
  return {
    outcome: COMPLETION_OUTCOME_LABEL[outcome as CompletionOutcome],
    pending: asked,
    completion: asked
      ? {
          items: (completion.items ?? []).filter((i): i is CompletionItem =>
            (COMPLETION_ITEMS as readonly string[]).includes(i),
          ),
          otherHeadings: completion.otherHeading
            ? [completion.otherHeading]
            : [],
        }
      : null,
  };
}

// Emelie's own names for the transfer's outcomes (TES-1054, Ö-284):
// "godkänt receptövertag, riktad komplettering, nytt blodprov, vårdmöte eller ytterligare medicinsk bedömning, Ella kan inte ta över behandlingen"
export const TRANSFER_OUTCOME_JOURNAL_LABEL: Record<string, string> = {
  approved: "Godkänt receptövertag",
  completion_requested: "Riktad komplettering",
  labs_required: "Nytt blodprov",
  meeting_required: "Vårdmöte eller ytterligare medicinsk bedömning",
  cannot_transfer: "Ella kan inte ta över behandlingen",
};

/**
 * The prescription transfer's decision as the review's dialog sends it
 * (transferReviewDecisionSchema's fields). A komplettering and a new blood
 * test wait for what they ask for; a meeting is its own next step.
 */
export function transferJournalDecision(decision: {
  outcome: string;
  reason?: string | null;
  carePath?: string | null;
  completion?: {
    items: readonly CompletionItem[];
    otherHeading?: string | null;
  } | null;
  prescription?: {
    choice: PrescriptionChoice;
    existingValidUntil?: string | null;
  } | null;
}): JournalDecision {
  const completion =
    decision.outcome === "completion_requested" && decision.completion
      ? {
          items: decision.completion.items,
          otherHeadings: decision.completion.otherHeading
            ? [decision.completion.otherHeading]
            : [],
        }
      : decision.outcome === "labs_required"
        ? { items: ["new_blood_test"] as CompletionItem[] }
        : null;
  return {
    outcome: TRANSFER_OUTCOME_JOURNAL_LABEL[decision.outcome] ?? null,
    pending:
      decision.outcome === "completion_requested" ||
      decision.outcome === "labs_required",
    completion,
    prescription:
      decision.outcome === "approved" ? (decision.prescription ?? null) : null,
    motivation: decision.reason ?? null,
    carePath:
      decision.outcome === "cannot_transfer"
        ? (decision.carePath ?? null)
        : null,
  };
}

interface JournalOrderParts {
  medication?: string | null;
  dose?: string | null;
  frequency?: string | null;
  duration?: string | null;
}

function orderKey(o: JournalOrderParts): string {
  return [o.medication, o.dose, o.frequency, o.duration]
    .map((p) => (p ?? "").trim().replace(/\s+/g, " ").toLowerCase())
    .join("|");
}

/**
 * TES-1095 (rule 3) — the order on screen is the order she is on now: same
 * drug, strength, frequency and length. An order without a dose on either
 * side is never "unchanged".
 */
export function monthlyTreatmentUnchanged(
  current: JournalOrderParts | null | undefined,
  onScreen: JournalOrderParts | null | undefined,
): boolean {
  if (!current?.dose?.trim() || !onScreen?.dose?.trim()) return false;
  return orderKey(current) === orderKey(onScreen);
}

/**
 * The monthly review's note is compiled before the doctor picks which
 * action publishes it, so it carries only what is decided on screen by
 * then: the prescription status chosen in the Webdoc step, and the
 * komplettering the review asked for, with its date.
 *
 * TES-1095 (rule 3) — and, when the order on screen is the one she is on
 * and the treatment continues (a new or the existing prescription), that the
 * treatment continues unchanged. Never with "Recept kan inte utfärdas ännu":
 * the treatment does not simply continue then.
 */
export function monthlyJournalDecision(input: {
  prescription?: {
    choice: PrescriptionChoice;
    existingValidUntil?: string | null;
  } | null;
  completion?: {
    requestedAt: string;
    items: readonly CompletionItem[];
    otherHeadings?: readonly string[];
  } | null;
  /** The order she is on now and the order on screen. */
  orders?: {
    current: JournalOrderParts | null | undefined;
    onScreen: JournalOrderParts | null | undefined;
  } | null;
}): JournalDecision {
  const continues =
    input.prescription?.choice === "new_issued" ||
    input.prescription?.choice === "existing_valid";
  return {
    treatmentUnchanged:
      continues &&
      monthlyTreatmentUnchanged(input.orders?.current, input.orders?.onScreen),
    prescription: input.prescription ?? null,
    completion: input.completion ?? null,
  };
}
